import { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import InputDateUTC from '../../../../FormControl/input/InputDateUTC';
import useFetchData from '../../../../../hooks/useFetchData';
import { useDispatch, useSelector } from 'react-redux';
import { updateFilter, showFilter } from '../../../../../store/actions/filter.actions';
import { useLocation, useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import InputField from '../../../../FormControl/input/InputField';
import SelectMultipleField from '../../../../FormControl/input/SelectMultipleField';
import { updateSkip } from '../../../../../store/actions/datatable.actions';
import DialogConfirmCancel from '../../../../dialog/DialogConfirm';
import { ASSESSMENT_TYPE_CUSTOM, ASSESSMENT_TYPE_STANDARD } from '../../constants/typeAssessment';
import SelectField from '../../../../FormControl/input/SelectField';

const FilterDialog = ({ module = 'dataEntryAssessment' }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const date = useSelector((state) => state?.date?.date || new Date());
  const filter = useSelector((state) => state?.filter[module]?.filter || {});
  const open = useSelector((state) => state?.filter[module]?.open || false);

  const { isLoading: isLoadingTemplate, data: dataTemplate, error: errorFetchTemplate } = useFetchData('data-entry/templates?type=assessment&skip=0&limit=100000&active=true');
  const { isLoading: isLoadingYears, data: dataYears, error: errorFetchYears } = useFetchData('setting/year');

  const dispatch = useDispatch();

  const [form, setInputForm] = useState(filter);
  const [error, setInputError] = useState({});

  useEffect(() => {
    if (!isEmpty(filter)) {
      setInputForm(filter);
    }
  }, [filter]);

  const onClose = () => {
    dispatch(showFilter(module, false));
  };

  const onChange = (name, value) => {
    if (name === 'templateId') {
      setInputError({ ...error, [name]: null, template: null });
      const template = dataTemplate.paginatedResults.filter((item) => value.includes(item._id));
      setInputForm({ ...form, [name]: value, template });
    } else if (name === 'yearLevelId') {
      setInputError({ ...error, [name]: null, yearLevel: null });
      const yearLevel = dataYears.filter((item) => value.includes(item._id));
      setInputForm({ ...form, [name]: value, yearLevel });
    } else if (name === 'flagId') {
      setInputError({ ...error, [name]: null, flag: null });
      const flag = dataFlags.filter((item) => value.includes(item._id));
      setInputForm({ ...form, [name]: value, flag });
    } else {
      setInputForm({ ...form, [name]: value });
      setInputError({ ...error, [name]: null });
    }
  };

  const onApply = () => {
    onClose();
    const filter = {
      ...form
    };

    dispatch(updateFilter(module, filter));
    dispatch(updateSkip(module, 0));
    navigate(pathname, { state: filter });
  };

  return (
    <DialogConfirmCancel
      id='data-entry-assessment-filter'
      open={open}
      title='Filter'
      maxWidth='xs'
      onCancel={onClose}
      onConfirm={onApply}
      textConfirm='Apply'
    >
      <Box className='flex flex-col gap-4'>
        <div className='grid grid-cols-3 items-center gap-4'>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Name
          </Typography>
          <div className='col-span-2'>
            <InputField
              id='form-filter-name'
              name='name'
              value={form.name}
              error={error.name}
              helperText={error.name}
              maxDate={date}
              onChange={(e) => onChange('name', e.target.value)}
            />
          </div>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Template
          </Typography>
          <div className='col-span-2'>
            <SelectMultipleField
              id='form-filter-template'
              name='templateId'
              value={form.templateId}
              error={error.templateId || errorFetchTemplate}
              helperText={error.templateId || errorFetchTemplate}
              onChange={(e) => onChange('templateId', e.target.value)}
              loading={isLoadingTemplate}
              options={dataTemplate?.paginatedResults?.map((item) => ({ value: item._id, label: item.title })) || []}
              placeholder='Choose template'
            />
          </div>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Type
          </Typography>
          <div className='col-span-2'>
            <SelectField
              id='form-filter-type'
              name='type'
              value={form.type}
              onChange={(e) => onChange('type', e.target.value)}
              className='capitalize '
              options={[
                { value: ASSESSMENT_TYPE_STANDARD, label: ASSESSMENT_TYPE_STANDARD, className: 'capitalize ' },
                { value: ASSESSMENT_TYPE_CUSTOM, label: ASSESSMENT_TYPE_CUSTOM, className: 'capitalize ' }
              ]}
              placeholder='Choose type'
            />
          </div>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Year level
          </Typography>
          <div className='col-span-2'>
            <SelectMultipleField
              id='form-filter-year-level'
              name='yearLevelId'
              value={form.yearLevelId}
              error={error.yearLevelId || errorFetchYears}
              helperText={error.yearLevelId || errorFetchYears}
              onChange={(e) => onChange('yearLevelId', e.target.value)}
              loading={isLoadingYears}
              options={dataYears?.map((item) => ({ value: item._id, label: item.name })) || []}
              placeholder='Choose a year level'
            />
          </div>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Sitting date
          </Typography>
          <InputDateUTC
            id='form-filter-sitting-date-from'
            name='sittingDateFrom'
            value={form.sittingDateFrom}
            error={error.sittingDateFrom}
            helperText={error.sittingDateFrom}
            onChange={(value) => onChange('sittingDateFrom', value)}
          />
          <InputDateUTC
            id='form-filter-sitting-date-to'
            name='sittingDateTo'
            value={form.sittingDateTo}
            error={error.sittingDateTo}
            helperText={error.sittingDateTo}
            isStartOfTheDay={false}
            minDate={form.sittingDateFrom}
            onChange={(value) => onChange('sittingDateTo', value)}
          />
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Due date
          </Typography>
          <InputDateUTC
            id='form-filter-due-date-from'
            name='dueDateFrom'
            value={form.dueDateFrom}
            error={error.dueDateFrom}
            helperText={error.dueDateFrom}
            onChange={(value) => onChange('dueDateFrom', value)}
          />
          <InputDateUTC
            id='form-filter-due-date-to'
            name='dueDateTo'
            value={form.dueDateTo}
            error={error.dueDateTo}
            helperText={error.dueDateTo}
            isStartOfTheDay={false}
            minDate={form.dueDateFrom}
            onChange={(value) => onChange('dueDateTo', value)}
          />
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Status
          </Typography>
          <div className='col-span-2'>
            <SelectField
              id='form-filter-status'
              name='status'
              value={form.status}
              onChange={(e) => onChange('status', e.target.value)}
              className='capitalize'
              options={[
                { value: 'Active', label: 'Active' },
                { value: 'Closed', label: 'Closed' }
              ]}
              placeholder='Choose status'
            />
          </div>
        </div>
      </Box>
    </DialogConfirmCancel>
  );
};

export default FilterDialog;
